import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Download, FileDownload, HelpOutline } from '@mui/icons-material';
import { Button, Typography, Box, Paper, CircularProgress, Alert } from '@mui/material';

import { COLORS } from 'constants/colors';
import { useAppDispatch } from 'utilities/hooks';
import { downloadSharedDocuments, getDownloadStatus, getSharedDownloadAvailability } from 'slices/documentsActions';
import { DownloadStatus } from 'interfaces/downloads';
import { isSharedDownloadUnavailableError } from 'utilities/shared-download';

const styles = {
    container: {
        display: 'flex',
        minHeight: '100vh',
        alignItems: 'center',
        flexDirection: 'column',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        px: 2,
    },
    paper: {
        p: 4,
        textAlign: 'center',
        maxWidth: 500,
        borderRadius: 4,
    },
    alert: {
        mb: 2,
    },
    errorAlert: {
        mb: 3,
    },
    description: {
        mb: 3,
    },
    iconBox: {
        mb: 3,
    },
    icon: {
        mb: 2,
        fontSize: 50,
        color: COLORS.DARK_NAVY,
    },
    buttonContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
    },
    downloadButton: {
        py: 1.5,
        color: COLORS.WHITE,
        backgroundColor: COLORS.DARK_NAVY,
        '&:hover': {
            backgroundColor: COLORS.PRIMARY_BLUE_HOVER,
        },
    },
    logo: {
        maxWidth: 100,
        height: 'auto',
    },
    logoContainer: {
        py: 2,
        cursor: 'pointer',
        backgroundColor: COLORS.DARK_NAVY,
        transition: 'background-color 0.3s',
        '&:hover': {
            backgroundColor: COLORS.PRIMARY_BLUE_HOVER,
        },
    },
} as const;

interface SharedDownloadPageProps { }

const SharedDownloadPage: React.FC<SharedDownloadPageProps> = () => {
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [senderName, setSenderName] = useState<string>('');
    const [isExpired, setIsExpired] = useState<boolean>(false);
    const [checkingAccess, setCheckingAccess] = useState<boolean>(true);
    const token = searchParams.get('token');

    useEffect(() => {
        let mounted = true;

        if (!token) {
            setError('No share token provided');
            setCheckingAccess(false);

            return () => {
                mounted = false;
            };
        }

        const urlParams = new URLSearchParams(window.location.search);
        const sender = urlParams.get('sender') || 'Someone';

        setSenderName(sender);
        setCheckingAccess(true);

        dispatch(getSharedDownloadAvailability(token))
            .unwrap()
            .then((status) => {
                if (mounted && !status.available) {
                    setIsExpired(true);
                }
            })
            .catch(() => {
                if (mounted) {
                    // Access checks fail closed. The download endpoint performs the
                    // same authoritative check before serving any document bytes.
                    setIsExpired(true);
                }
            })
            .finally(() => {
                if (mounted) {
                    setCheckingAccess(false);
                }
            });

        return () => {
            mounted = false;
        };
    }, [dispatch, token]);

    const handleDownload = async () => {
        if (!token) {
            setError('No share token provided');
            return;
        }

        setDownloading(true);
        setError(null);

        try {
            const availability = await dispatch(getSharedDownloadAvailability(token)).unwrap();
            if (!availability.available) {
                setIsExpired(true);
                setDownloading(false);

                return;
            }

            const response = await dispatch(downloadSharedDocuments(token)).unwrap();
            if (response && response.signature) {
                const pollStatus = async () => {
                    try {
                        const statusResponse = await dispatch(
                            getDownloadStatus({
                                signature: response.signature,
                                token,
                            }),
                        ).unwrap();
                        if (
                            statusResponse &&
                            [DownloadStatus.FAILED, DownloadStatus.READY].includes(statusResponse.status)
                        ) {
                            if (statusResponse.status === DownloadStatus.READY && statusResponse.download_url) {
                                // Download is ready, trigger download
                                const link = document.createElement('a');
                                link.href = statusResponse.download_url;
                                // TODO: review this
                                link.setAttribute('download', 'shared-files.zip');
                                document.body.appendChild(link);
                                link.click();
                                link.remove();
                                setDownloading(false);
                            } else if (statusResponse.status === DownloadStatus.FAILED) {
                                setError(statusResponse.message || 'Download failed. Please try again.');
                                setDownloading(false);
                            }
                        } else {
                            setTimeout(pollStatus, 2000);
                        }
                    } catch (error: any) {
                        if (isSharedDownloadUnavailableError(error)) {
                            setIsExpired(true);
                            setDownloading(false);

                            return;
                        }

                        const errorMessage =
                            error?.data?.message || 'Failed to check download status. Please try again.';

                        setError(errorMessage);
                        setDownloading(false);
                    }
                };

                // Start the polling
                pollStatus();
            } else {
                setDownloading(false);
            }
        } catch (error: any) {
            if (isSharedDownloadUnavailableError(error)) {
                setIsExpired(true);
                setDownloading(false);

                return;
            }

            const errorMessage = error?.data?.message || 'Failed to download files. Please try again.';

            setError(errorMessage);
            setDownloading(false);
        }
    };

    const handleLogoClick = () => {
        window.open('https://sitemaxsystems.com', '_blank');
    };

    if (isExpired) {
        return (
            <Box sx={styles.container}>
                <Paper elevation={3} sx={{ ...styles.paper, p: 0, overflow: 'hidden' }}>
                    <Box sx={styles.logoContainer} onClick={handleLogoClick}>
                        <img
                            style={styles.logo}
                            src="/images/sitemax-logo-2022.svg"
                            alt="SiteMax Systems: Comprehensive Construction & Project Management Software"
                        />
                    </Box>
                    <Box sx={{ p: 4 }}>
                        <Box sx={styles.iconBox}>
                            <HelpOutline sx={styles.icon} />
                            <Typography variant="h4" component="h1" gutterBottom>
                                Files No Longer Available
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                These shared files are no longer available for download. Contact{' '}
                                {senderName || 'the sender'} for a new link.
                            </Typography>
                        </Box>
                    </Box>
                </Paper>
            </Box>
        );
    }

    if (!token) {
        return (
            <Box sx={styles.container}>
                <Paper elevation={3} sx={{ ...styles.paper, p: 0, overflow: 'hidden' }}>
                    <Box sx={styles.logoContainer} onClick={handleLogoClick}>
                        <img
                            style={styles.logo}
                            src="/images/sitemax-logo-2022.svg"
                            alt="SiteMax Systems: Comprehensive Construction & Project Management Software"
                        />
                    </Box>
                    <Box sx={{ p: 4 }}>
                        <Alert severity="error" sx={styles.alert}>
                            Invalid Share Link
                        </Alert>
                        <Typography variant="body1" color="text.secondary" sx={styles.description}>
                            This share link is invalid or has expired. Please contact the person who shared these files with you.
                        </Typography>
                        <Button
                            variant="outlined"
                            onClick={() => window.close()}
                        >
                            Close
                        </Button>
                    </Box>
                </Paper>
            </Box>
        );
    }

    if (checkingAccess) {
        return (
            <Box sx={styles.container}>
                <Paper elevation={3} sx={styles.paper}>
                    <CircularProgress aria-label="Checking shared files availability" />
                    <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                        Checking shared files availability...
                    </Typography>
                </Paper>
            </Box>
        );
    }

    return (
        <Box sx={styles.container}>
            <Paper elevation={3} sx={{ ...styles.paper, p: 0, overflow: 'hidden' }}>
                <Box sx={styles.logoContainer} onClick={handleLogoClick}>
                    <img
                        style={styles.logo}
                        src="/images/sitemax-logo-2022.svg"
                        alt="SiteMax Systems: Comprehensive Construction & Project Management Software"
                    />
                </Box>
                <Box sx={{ p: 4 }}>
                    <Box sx={styles.iconBox}>
                        <FileDownload sx={styles.icon} />
                        <Typography variant="h4" component="h1" gutterBottom>
                            Shared Files
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            {senderName} shared files with you via SiteMax.
                        </Typography>
                    </Box>

                    {
                        error ?
                            <Alert severity="error" sx={styles.errorAlert}>
                                {error}
                            </Alert> : null
                    }

                    <Box sx={styles.buttonContainer}>
                        <Button
                            size="large"
                            variant="contained"
                            disabled={downloading}
                            onClick={handleDownload}
                            sx={styles.downloadButton}
                            startIcon={downloading ? <CircularProgress size={20} color="inherit" /> : <Download />}
                        >
                            {downloading ? 'Preparing Download...' : 'Download Files'}
                        </Button>

                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                            Files will be downloaded as a compressed ZIP file.
                        </Typography>
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
};

export default SharedDownloadPage;
